import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listResource, getResource, createResource, updateResource } from '@diggens/api-client';
import type { Fund } from '@diggens/types';

export function useFunds(tenantId?: string) {
  return useQuery({
    queryKey: ['funds', tenantId],
    queryFn: async () => {
      const params: Record<string, string | undefined> = { tenant_id: tenantId };
      const { data } = await listResource<Fund>('funds', params);
      return data;
    },
  });
}

export function useFund(fundId: string) {
  return useQuery({
    queryKey: ['funds', fundId],
    queryFn: () => getResource<Fund>('funds', fundId),
    enabled: !!fundId,
  });
}

export function useCreateFund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { tenant_id: string; name: string; external_id?: string | null; reporting_frequency?: 'monthly' | 'quarterly' | 'annual' | null }) =>
      createResource<Fund>('funds', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funds'] });
    },
  });
}

export function useUpdateFund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { fundId: string; updates: Partial<Fund> }) =>
      updateResource<Fund>('funds', variables.fundId, variables.updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['funds'] });
      queryClient.invalidateQueries({ queryKey: ['funds', variables.fundId] });
    },
  });
}
